// AI Ghostwriter Pro - FAQ Page
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { Button } from '../components/ui/button';
import { ChevronDown } from 'lucide-react';

const FAQ_ITEMS = [
  { q: 'How does AI ghostwriting work?', a: 'You provide your book concept, genre, and preferences. Our AI creates a detailed chapter-by-chapter outline for your approval, then writes each chapter maintaining context awareness throughout. You review and request revisions until satisfied. The entire process takes 7-14 days depending on your package.' },
  { q: 'Will my AI-written book be unique?', a: 'Yes, each book is generated fresh based on your specific inputs. No templates, no recycled content. Your book is 100% original and passes all plagiarism checks. We verify every manuscript with professional plagiarism detection tools.' },
  { q: 'How much does AI ghostwriting cost?', a: 'Our packages range from $497 for a 20,000-word ebook (Starter) to $1,497 for a complete 70,000-word book with full publishing support (Complete). All packages include full copyright ownership and Amazon optimization.' },
  { q: 'Is AI ghostwriting legal?', a: 'Yes, AI ghostwriting is completely legal. You own full rights to the content generated for you. You can publish it under your name on Amazon, Barnes & Noble, or any retailer. Major publishers and platforms like Amazon KDP accept AI-assisted content.' },
  { q: 'What genres can AI write?', a: 'Our AI excels at Romance, Mystery/Thriller, Science Fiction, Fantasy, Self-Help, Business, Biography/Memoir, How-To guides, and more. The system adapts its writing style to match genre conventions and reader expectations.' },
  { q: 'How is this different from ChatGPT?', a: 'AI Ghostwriter Pro uses a professional multi-step pipeline: market research, structured outlines, chapter-by-chapter writing with full context awareness, professional editing passes, and Amazon optimization. ChatGPT gives you a draft. We give you a publication-ready book.' },
  { q: 'Do I own the content?', a: '100%. You own full copyright to everything we create. There are no royalty splits, no attribution requirements. Publish under your name, anywhere you want.' },
  { q: 'What if I\'m not satisfied?', a: 'Each package includes revision rounds (1 for Starter, 3 for Professional, unlimited for Complete). If you\'re not satisfied after reviewing the first 3 chapters, we offer a full refund. No questions asked.' },
  { q: 'How long does the process take?', a: 'Starter packages: 3-5 days. Professional: 7-10 days. Complete: 10-14 days. These timelines include outline creation, writing, and one round of revisions.' },
  { q: 'Can I provide my own outline?', a: 'Absolutely! If you already have a chapter outline, you can provide it during project setup. The AI will follow your structure while adding professional touches and ensuring narrative consistency.' },
  { q: 'What format will I receive?', a: 'You receive your manuscript in DOCX format, ready for formatting. Complete package also includes a Kindle-ready EPUB file and print-ready PDF.' },
  { q: 'Do you offer refunds?', a: 'Yes. If you\'re not satisfied after the outline stage, we refund 100%. After the first 3 chapters, we offer a full refund if the quality doesn\'t meet your expectations. After full delivery, refunds are handled on a case-by-case basis.' }
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQ_ITEMS.map(item => ({
    "@type": "Question",
    "name": item.q,
    "acceptedAnswer": { "@type": "Answer", "text": item.a }
  }))
};

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-700 rounded-xl overflow-hidden" data-testid="faq-item">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-800/50 transition-colors"
      >
        <span className="text-white font-medium pr-4">{item.q}</span>
        <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-gray-400 leading-relaxed border-t border-slate-700/50 pt-4">
          {item.a}
        </div>
      )}
    </div>
  );
}

export function GhostwriterFAQ() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SeoHead
        title="FAQ | AI Ghostwriter Pro - Your Questions Answered"
        description="Frequently asked questions about AI ghostwriting. Learn about pricing, process, ownership, refunds, genres, and more. Get answers before starting your book."
        canonical="https://aighostwriterpro.com/faq"
        og={{ type: 'website', url: 'https://aighostwriterpro.com/faq', title: 'AI Ghostwriter Pro FAQ', description: 'Your questions about AI ghostwriting answered.', siteName: 'AI Ghostwriter Pro' }}
        twitter={{ card: 'summary', title: 'AI Ghostwriter Pro FAQ', description: 'Your questions about AI ghostwriting answered.' }}
        schemas={[FAQ_SCHEMA]}
      />

      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="faq-heading">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Everything you need to know about our AI ghostwriting service.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {FAQ_ITEMS.map((item, i) => (
            <FaqItem key={i} item={item} />
          ))}
        </div>

        <div className="text-center mt-16 space-y-4">
          <p className="text-gray-400">Still have questions?</p>
          <div className="flex justify-center gap-4">
            <Link to="/contact">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800" data-testid="faq-contact-btn">
                Contact Us
              </Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-testid="faq-pricing-btn">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GhostwriterFAQ;
